<template>
	<v-card class="access-groups fill-wh">
		<v-toolbar class="toolbar">
			<v-btn v-if="!!selectedGroupId" icon @click="selectedGroupId = null">
				<v-icon>fas fa-times</v-icon>
			</v-btn>

			<v-toolbar-title>
				{{ selectedGroupId ? selectedGroup.name : "No group selected" }}
			</v-toolbar-title>

			<v-spacer />

			<v-btn
				v-if="!!selectedGroupId && canUpdate"
				:disabled="!allowRename"
				icon
				@click="showRenameGroupDialog"
			>
				<v-icon>fas fa-pencil-alt</v-icon>
			</v-btn>
		</v-toolbar>

		<div class="master-detail-container">
			<layout-master-detail :show-details="showDetails">
				<AccessGroupList
					:items="groups"
					:selected.sync="selectedGroupId"
					@rename:group="showRenameGroupDialog"
				/>

				<template #detail>
					<v-list>
						<CommunityList
							:items="communities"
							:selected.sync="selectedCommunities"
							@add:community="showAddCommunityDialog()"
							@remove:communities="removeCommunities"
						/>

						<UserList
							:items="users"
							:selected.sync="selectedUser"
							@add:user="showAddUserDialog()"
							@remove:users="removeUsers"
						/>
					</v-list>
				</template>
			</layout-master-detail>
		</div>

		<!-- Speed Dial -->
		<AGSpeedDial
			:show-add-group="canCreate"
			:show-create-user="true"
			@action:add-group="showAddGroupDialog()"
			@action:create-user="showCreateUserDialog()"
		/>

		<!-- Dialogs -->
		<AddGroupDialog ref="addGroupDialog" />
		<RenameGroupDialog
			ref="renameGroupDialog"
			:placeholder="selectedGroup && selectedGroup.name"
		/>
		<AddCommunityDialog ref="addCommunityDialog" :communities="communities" />
		<AddUserDialog ref="addUserDialog" :users="users" />

		<CreateUserDialog ref="createUserDialog" />
	</v-card>
</template>

<style lang="scss" scoped>
.master-detail-container {
	position: relative;
	min-height: calc(100% - 56px); // 56px toolbar

	@media #{map-get($display-breakpoints, 'md-and-up')} {
		min-height: calc(100% - 64px); // 64px tool bar
	}
}
</style>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

import AccessGroupList from "./access-groups/AccessGroupList";
import CommunityList from "./access-groups/CommunityList";
import UserList from "./access-groups/UserList";
import AGSpeedDial from "./access-groups/AccessGroupSpeedDial";

import AddGroupDialog from "./access-groups/AddGroupDialog";
import CreateUserDialog from "./access-groups/CreateUserDialog";
import RenameGroupDialog from "./access-groups/RenameGroupDialog";
import AddCommunityDialog from "./access-groups/AddCommunityDialog";
import AddUserDialog from "./access-groups/AddUserDialog";

export default {
	components: {
		AccessGroupList,
		CommunityList,
		UserList,
		AGSpeedDial,

		AddGroupDialog,
		CreateUserDialog,
		RenameGroupDialog,
		AddCommunityDialog,
		AddUserDialog,
	},

	computed: {
		allowRename() {
			// User should never be allowed to rename 'All Communities' group.
			const selectedGroup = this.selectedGroup;
			const groupName = _.get(selectedGroup, "name");
			return !(groupName === "All Communities");
		},

		// CASL
		// ----

		canCreate() {
			return this.$can("create", "AccessGroups");
		},
		canUpdate() {
			return this.$can("update", "AccessGroups");
		},
		canAddCommunity() {
			return this.$can("addCommunity", "AccessGroups");
		},
		canAddUser() {
			return this.$can("addUser", "AccessGroups");
		},

		// GROUPS
		// ------
		...mapGetters({ groups: "accessGroups/groups" }),
		selectedGroup() {
			return this.$store.getters["accessGroups/selectedGroup"];
		},
		selectedGroupId: {
			get() {
				return this.$store.getters["accessGroups/selectedGroupId"];
			},
			set(value) {
				this.$store.dispatch("accessGroups/selectGroup", value);
			},
		},

		// COMMUNITY
		// ---------
		...mapGetters({ communities: "accessGroups/communities" }),
		selectedCommunities: {
			get() {
				return this.$store.getters["accessGroups/selectedCommunityIds"];
			},
			set(value) {
				this.$store.dispatch("accessGroups/selectCommunities", value);
			},
		},

		// USER
		// ----
		...mapGetters({ users: "accessGroups/users" }),
		selectedUser: {
			get() {
				return this.$store.getters["accessGroups/selectedUserIds"];
			},
			set(value) {
				this.$store.dispatch("accessGroups/selectUsers", value);
			},
		},

		// ----------

		showDetails() {
			return !!this.selectedGroupId;
		},
	},

	created() {
		this.getGroups();
	},

	methods: {
		// GROUPS
		// ------
		...mapActions({
			getGroups: "accessGroups/getGroups",
			addGroup: "accessGroups/addGroup",
			renameGroup: "accessGroups/renameGroup",
		}),
		showAddGroupDialog() {
			this.$refs.addGroupDialog.pop(this.addGroup);
		},
		showRenameGroupDialog() {
			this.$refs.renameGroupDialog.pop(this.renameGroup);
		},

		// COMMUNITIES
		// -----------
		...mapActions({
			addCommunities: "accessGroups/addCommunitiesToGroup",
			removeCommunities: "accessGroups/removeCommunitiesFromGroup",
		}),
		showAddCommunityDialog() {
			this.$refs.addCommunityDialog.pop(this.addCommunities);
		},

		// USERS
		// -----
		...mapActions({
			createUser: "accessGroups/createUserInGroup",
			addUsers: "accessGroups/addUsersToGroup",
			removeUsers: "accessGroups/removeUsersFromGroup",
		}),
		// Create a new user and associate with existing access group
		showCreateUserDialog() {
			this.$refs.createUserDialog.pop(this.createUser);
		},
		// Add an existing user to an existing access group
		showAddUserDialog() {
			this.$refs.addUserDialog.pop(this.addUsers);
		},
	},
};
</script>
